import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { designOf, type DesignDef, type DesignId, type Density, type Mode } from './designs';

export interface Prefs { tasarim: DesignId; mod: Mode; yogunluk: Density }
export interface ServerPrefs extends Prefs { sirket: Prefs; kisisel: boolean }

const LS = 'dolphin.gorunum';
const DEFAULT: Prefs = { tasarim: 'aurora', mod: 'acik', yogunluk: 'normal' };

function readLocal(): Prefs {
  try {
    const v = JSON.parse(localStorage.getItem(LS) ?? 'null');
    if (v?.tasarim) return { ...DEFAULT, ...v };
    // eski sürüm: yalnız açık/koyu tema
    const t = localStorage.getItem('dolphin.theme');
    if (t) return { ...DEFAULT, mod: t === 'dark' ? 'koyu' : 'acik' };
  } catch { /* */ }
  return DEFAULT;
}

const systemDark = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;

/** html etiketine data-design / data-theme / data-density yazar, yazı tiplerini yükler */
function apply(p: Prefs) {
  const d = designOf(p.tasarim);
  const dark = p.mod === 'koyu' || (p.mod === 'sistem' && systemDark());
  const el = document.documentElement;
  el.dataset.design = d.id;
  el.dataset.theme = dark ? 'dark' : 'light';
  el.dataset.density = p.yogunluk;
  el.dataset.layout = d.layout;
  Promise.all(d.fonts()).catch(() => undefined);
  return dark;
}

// İlk boyamadan önce (giriş ekranı dahil) son kullanılan görünüm
apply(readLocal());

interface Ctx {
  prefs: Prefs;
  design: DesignDef;
  dark: boolean;
  /** sunucudaki tercihler (şirket varsayılanı dahil) */
  server: ServerPrefs | null;
  /** önizleme (kaydetmeden) */
  preview: (p: Partial<Prefs>) => void;
  /** kaydet: kullanıcıya özel */
  save: (p: Partial<Prefs>) => Promise<void>;
  /** yönetici: şirket varsayılanı */
  saveCompany: (p: Partial<Prefs>) => Promise<void>;
  reset: () => Promise<void>;
  /** oturum açılışında /auth/me içindeki tercihleri uygula */
  hydrate: (s: ServerPrefs | null | undefined) => void;
  /** grafiklerde kullanılacak çözülmüş renkler */
  tokens: Tokens;
}

export interface Tokens { chart: string[]; accent: string; muted: string; grid: string; text: string; panel: string; success: string; warning: string; danger: string }

const C = createContext<Ctx | null>(null);

function readTokens(): Tokens {
  const cs = getComputedStyle(document.documentElement);
  const v = (n: string, f: string) => cs.getPropertyValue(n).trim() || f;
  return {
    chart: [1, 2, 3, 4, 5].map((i) => v(`--chart-${i}`, ['#6366f1', '#a855f7', '#ec4899', '#f59e0b', '#10b981'][i - 1])),
    accent: v('--accent', '#6366f1'), muted: v('--muted', '#94a3b8'), grid: v('--chart-grid', 'rgba(148,163,184,.25)'),
    text: v('--text', '#0f172a'), panel: v('--panel', '#fff'), success: v('--success', '#10b981'), warning: v('--warning', '#f59e0b'), danger: v('--danger', '#ef4444'),
  };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<Prefs>(readLocal);
  const [server, setServer] = useState<ServerPrefs | null>(null);
  const [dark, setDark] = useState(() => document.documentElement.dataset.theme === 'dark');
  const [tokens, setTokens] = useState<Tokens>(readTokens);

  useEffect(() => {
    setDark(apply(prefs));
    try { localStorage.setItem(LS, JSON.stringify(prefs)); } catch { /* */ }
    // stiller uygulandıktan sonra token'ları oku
    const id = requestAnimationFrame(() => setTokens(readTokens()));
    return () => cancelAnimationFrame(id);
  }, [prefs]);

  useEffect(() => {
    if (prefs.mod !== 'sistem' || typeof matchMedia === 'undefined') return;
    const m = matchMedia('(prefers-color-scheme: dark)');
    const f = () => { setDark(apply(prefs)); setTokens(readTokens()); };
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, [prefs]);

  const hydrate = useCallback((s: ServerPrefs | null | undefined) => {
    if (!s) return;
    setServer(s);
    setPrefs({ tasarim: s.tasarim, mod: s.mod, yogunluk: s.yogunluk });
  }, []);

  const preview = useCallback((p: Partial<Prefs>) => setPrefs((o) => ({ ...o, ...p })), []);
  const save = useCallback(async (p: Partial<Prefs>) => {
    setPrefs((o) => ({ ...o, ...p }));
    hydrate(await api<ServerPrefs>('/tercih', { method: 'PUT', body: p }));
  }, [hydrate]);
  const saveCompany = useCallback(async (p: Partial<Prefs>) => {
    const s = await api<ServerPrefs>('/tercih/sirket', { method: 'PUT', body: p });
    setServer(s);
  }, []);
  const reset = useCallback(async () => hydrate(await api<ServerPrefs>('/tercih/sifirla', { method: 'PUT' })), [hydrate]);

  const value = useMemo<Ctx>(() => ({ prefs, design: designOf(prefs.tasarim), dark, server, preview, save, saveCompany, reset, hydrate, tokens }),
    [prefs, dark, server, preview, save, saveCompany, reset, hydrate, tokens]);
  return <C.Provider value={value}>{children}</C.Provider>;
}

export function useTheme() {
  const c = useContext(C);
  if (!c) throw new Error('ThemeProvider eksik');
  return c;
}
